/**
 * Global Activity — embedded as a SECTION on the Overview tab (NOT its
 * own tab). Provides a metric toggle, world choropleth map, and a
 * country leaderboard table. The "View full global activity →" link
 * deep-links to the Trends tab with `groupBy=country` preselected.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import clsx from "clsx";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, Button } from "@/components/primitives";
import { fmtNumber } from "@/lib/format";
import { countryName, NUM_TO_ISO2, WORLD_ATLAS_URL } from "@/config/geo";
import type { CountryActivityV1, GlobalActivityMetricV1 } from "@/types/v1";

const METRICS: { key: GlobalActivityMetricV1; label: string }[] = [
  { key: "total",             label: "Total" },
  { key: "highRisk",          label: "High risk" },
  { key: "suspicious",        label: "Suspicious" },
  { key: "managedList",       label: "Managed list" },
  { key: "loginAbuse",        label: "Login abuse" },
  { key: "registrationAbuse", label: "Registration abuse" },
  { key: "paymentAbuse",      label: "Payment abuse" },
];

function navigateToTrendsByCountry() {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", "trends");
  url.searchParams.set("groupBy", "country");
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function GlobalActivitySection() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const [metric, setMetric] = useState<GlobalActivityMetricV1>("total");

  const q = useQuery({
    queryKey: ["globalActivity", filters, metric, tick],
    queryFn: ({ signal }) => api.globalActivity(filters, metric, signal),
  });

  const rows = q.data?.data ?? [];
  const max = useMemo(() => rows.reduce((m, r) => Math.max(m, r[metric] || 0), 0), [rows, metric]);
  const byIso2 = useMemo(() => {
    const m = new Map<string, CountryActivityV1>();
    rows.forEach((r) => m.set(r.country, r));
    return m;
  }, [rows]);

  return (
    <Card>
      <CardHeader
        title="Global activity"
        subtitle="Country roll-up by selected metric — sampled and weighted"
        action={
          <div className="flex items-center gap-2">
            <MetricToggle value={metric} onChange={setMetric} />
            <Button variant="ghost" size="sm" onClick={navigateToTrendsByCountry}>
              View full global activity →
            </Button>
          </div>
        }
      />
      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="lg:col-span-2 h-80" />
          <Skeleton className="h-80" />
        </div>
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No country data in this window." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-lg border border-border bg-surface-2 p-2">
            <WorldMap byIso2={byIso2} max={max} metric={metric} />
          </div>
          <CountryTable rows={rows} metric={metric} max={max} />
        </div>
      )}
    </Card>
  );
}

function MetricToggle({ value, onChange }: { value: GlobalActivityMetricV1; onChange: (m: GlobalActivityMetricV1) => void }) {
  return (
    <div className="inline-flex flex-wrap overflow-hidden rounded-md border border-border-strong">
      {METRICS.map((m) => (
        <button
          key={m.key}
          type="button"
          onClick={() => onChange(m.key)}
          className={clsx(
            "px-2.5 py-1 text-[11px]",
            value === m.key ? "bg-surface-3 text-fg" : "text-fg-muted hover:bg-surface-2 hover:text-fg",
          )}
        >{m.label}</button>
      ))}
    </div>
  );
}

function colorFor(value: number, max: number): string {
  if (!value || !max) return "rgb(var(--surface-3))";
  const t = Math.min(1, Math.log10(value + 1) / Math.log10(max + 1));
  // accent → high gradient
  const r = Math.round(74  + (244 - 74)  * t);
  const g = Math.round(150 + (95  - 150) * t);
  const b = Math.round(255 + (109 - 255) * t);
  return `rgb(${r},${g},${b})`;
}

function WorldMap({ byIso2, max, metric }: { byIso2: Map<string, CountryActivityV1>; max: number; metric: GlobalActivityMetricV1 }) {
  return (
    <div className="h-80 w-full">
      <ComposableMap projectionConfig={{ scale: 145 }} width={800} height={400} style={{ width: "100%", height: "100%" }}>
        <Geographies geography={WORLD_ATLAS_URL}>
          {({ geographies }: { geographies: any[] }) =>
            geographies.map((geo) => {
              const iso2 = NUM_TO_ISO2[geo.id as keyof typeof NUM_TO_ISO2];
              const row = iso2 ? byIso2.get(iso2) : undefined;
              const v = row?.[metric] ?? 0;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={colorFor(v, max)}
                  stroke="rgb(var(--border))"
                  strokeWidth={0.4}
                  style={{
                    default: { outline: "none" },
                    hover:   { outline: "none", fill: "rgb(var(--accent))", cursor: row ? "pointer" : "default" },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
}

function CountryTable({ rows, metric, max }: { rows: CountryActivityV1[]; metric: GlobalActivityMetricV1; max: number }) {
  const top = rows.slice(0, 12);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-2">
      <table className="w-full text-sm">
        <thead className="bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Country</th>
            <th className="px-3 py-2 text-right font-medium">{METRICS.find((m) => m.key === metric)?.label}</th>
            <th className="px-3 py-2 text-right font-medium w-1/3">Share</th>
          </tr>
        </thead>
        <tbody>
          {top.map((r) => {
            const v = r[metric];
            const pct = max ? v / max : 0;
            return (
              <tr key={r.country} className="border-t border-border hover:bg-surface-3">
                <td className="px-3 py-2 text-fg">
                  <span className="font-mono text-fg-muted">{r.country}</span>
                  <span className="ml-2 text-fg-muted">{countryName(r.country)}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-fg">{fmtNumber(v)}</td>
                <td className="px-3 py-2">
                  <div className="h-1.5 w-full rounded-full bg-surface-3">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${Math.round(pct * 100)}%`, background: colorFor(v, max) }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
